import { Integration, WorkspaceElementState } from "@membranehq/sdk";
import {
  computed,
  ref,
  shallowRef,
  watch,
  type ComputedRef,
  type Ref,
} from "vue";

import type {
  Controller,
  IntegrationReport,
  IntegrationReportIntegrationConfigDictionary,
  NotionConfig,
  OrgId,
  Profile,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import NotionIcon from "@/components/icons/apps/NotionIcon.vue";
import ZapierIcon from "@/components/icons/apps/ZapierIcon.vue";
import type { UseIntegrations } from "@/components/integrations/useIntegrations";
import { releaseBestEffort } from "@/ref";
import { RouteNames } from "@/routes/types";
import type { Router } from "@/types";
import { in_electron } from "@/utils/in";
import { navigateTo } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";

import {
  dataCollectionsPerIntegration,
  isIntegrationAllowed,
} from "./integrationAllowlist";
import type { IntegrationItem, IntegrationNature } from "./types";

/**
 * Membrane items all share the `membrane` type, which defaults to the "ai"
 * nature. These Membrane keys should instead be tagged as tasks because they
 * sync external tasks into Routine.
 */
const taskIntegrationKeys = new Set([
  "asana",
  "attio",
  "clickup",
  "featurebase",
  "github",
  "gitlab",
  "gmail",
  "google-tasks",
  "linear",
  "jira",
  "microsoft-outlook",
  "microsoft-to-do",
  "microsoft-planner",
  "microsoft-teams",
  "monday",
  "notion",
  "slack",
  "ticktick",
  "todoist",
  "trello",
  "whatsapp",
]);

const natureForApp = (app: Integration): IntegrationNature | undefined =>
  app.key && taskIntegrationKeys.has(app.key) ? "tasks" : undefined;

export type MembraneListResult = {
  loading: Ref<boolean>;
  error: ComputedRef<unknown>;
  connectedItems: ComputedRef<IntegrationItem[]>;
  availableItems: ComputedRef<IntegrationItem[]>;
  /** Number of Membrane connections across the organization. */
  connectionsCount: ComputedRef<number>;
  isPersonalOrg: Ref<boolean>;
  openUrl: (url: string) => void;
  /**
   * Refresh the underlying connections list so that the connected/available
   * card split stays in sync after the user adds, reconnects or deletes a
   * connection from the detail screen.
   */
  refreshConnections: () => Promise<void>;
  /**
   * Releases the profiles/integrations controller refs once/if they have
   * loaded. Idempotent.
   */
  release: () => void;
};

/**
 * Synchronous composable that returns reactive Membrane app list data.
 * All fetching happens in the background — the returned refs populate
 * asynchronously as data arrives.
 *
 * Reacts to `orgId` changes (works even if orgId starts undefined).
 */
export function useMembraneList(
  controller: Controller,
  router: Router,
  integrationsStore: UseIntegrations,
  orgId: Ref<string | undefined>
): MembraneListResult {
  const organizationId = computed<OrgId | undefined>(() => orgId.value);

  const loading = ref(true);

  const isPersonalOrg = ref(false);

  // Hold on to the releasable refs (instead of `.release()`-ing them right
  // away) so that mutations — e.g. connecting/disconnecting a Notion
  // workspace — propagate back through the controller ref and the
  // connected/available split stays in sync without a full app relaunch.
  const profilesSource = shallowRef<ReleasableRef<Profile[]> | null>(null);
  const integrationsSource =
    shallowRef<ReleasableRef<IntegrationReportIntegrationConfigDictionary> | null>(
      null
    );

  const stopHandles: Array<() => void> = [];
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    for (const stop of stopHandles) stop();
    releaseBestEffort(profilesSource.value);
    releaseBestEffort(integrationsSource.value);
  };

  void Promise.all([
    controller.auth.profiles(),
    controller.integration.list(),
  ]).then(([profilesRef, integrationsRef]) => {
    if (released) {
      // The caller released before the load finished: drop the refs
      // immediately instead of leaking them.
      releaseBestEffort(profilesRef);
      releaseBestEffort(integrationsRef);
      return;
    }
    profilesSource.value = profilesRef;
    integrationsSource.value = integrationsRef;
  });

  const identities = computed<Profile[]>(
    () => profilesSource.value?.value ?? []
  );
  const routineIntegrations =
    computed<IntegrationReportIntegrationConfigDictionary>(
      () => integrationsSource.value?.value ?? {}
    );

  stopHandles.push(
    watch(
      orgId,
      async (id) => {
        if (!id) {
          isPersonalOrg.value = false;
          return;
        }
        const orgRef = await controller.organization.get(id);
        isPersonalOrg.value = orgRef.release()?.personal === true;
      },
      { immediate: true }
    )
  );

  // --- Controller-backed integrations (reactive) ---

  const gsuiteIdentities = computed(() =>
    identities.value.filter(
      (profile) =>
        profile.kind.provider === "google" &&
        !profile.kind.identity.endsWith("@gmail.com")
    )
  );

  const isGoogleWorkspaceActive = computed(() =>
    gsuiteIdentities.value.some(
      (profile) =>
        profile.kind.provider === "google" &&
        profile.kind.scopes.contact_directory === true
    )
  );

  const notionIntegrations = computed(() =>
    Object.entries(routineIntegrations.value).filter(
      (
        integration
      ): integration is [
        string,
        NotionConfig<IntegrationReport> & { type: "notion" },
      ] => integration[1].type === "notion"
    )
  );

  const isNotionActive = computed(
    () => in_electron && notionIntegrations.value.length > 0
  );

  // --- Navigation helpers ---

  // Prefer the orgId from the current route over the global selection so that
  // clicking an integration card always keeps the user on the org whose
  // settings they are currently viewing (e.g. when they reached the page via
  // the sidebar gear icon, which doesn't update the global selection).
  const navigationOrgId = (): string | undefined => {
    const routeOrgId = router.currentRoute.value.params.orgId;
    if (typeof routeOrgId === "string" && routeOrgId) return routeOrgId;
    return orgId.value;
  };

  const configureNotion = () => {
    const id = navigationOrgId();
    if (!id) return;
    navigateTo(RouteNames.SettingsIntegrationsNotion, router, {
      params: { orgId: id },
    });
  };

  const configureGoogleWorkspace = () => {
    const id = navigationOrgId();
    if (!id) return;
    navigateTo(RouteNames.SettingsIntegrationsGoogleWorkspace, router, {
      params: { orgId: id },
    });
  };

  const openUrl = (url: string) => openExternalUrl(url, controller);

  const manageApp = (appId: string) => {
    const id = navigationOrgId();
    if (!id) return;
    const currentApp = allApps.value.find((app) => app.id === appId);
    if (!currentApp?.key) return;
    navigateTo(RouteNames.SettingsMembraneIntegration, router, {
      params: { orgId: id, appKey: currentApp.key },
    });
  };

  // --- Membrane API ---
  // The app-wide integrations store already holds every organization's
  // services + connections, so we derive everything from its refs instead of
  // triggering a second round of API calls.

  const integrations = integrationsStore.forOrganization(organizationId);

  // External MCP is managed on its own settings screen (CustomMCPClients),
  // not listed as integration cards. Refresh still runs so composer pills
  // stay in sync after Membrane reconnects.
  const mcp = integrationsStore.mcp;

  const allApps = computed(() =>
    integrations.services.value
      .filter((app) => app.state === WorkspaceElementState.READY)
      .filter((app) => isIntegrationAllowed(app.key))
  );

  const enabledAppIds = computed(() => {
    const ids = new Set(
      integrations.connections.value.map(({ integrationId }) => integrationId)
    );
    return ids;
  });

  const enabledApps = computed(() =>
    allApps.value.filter((app) => enabledAppIds.value.has(app.id))
  );

  const disabledApps = computed(() =>
    allApps.value.filter((app) => !enabledAppIds.value.has(app.id))
  );

  stopHandles.push(
    watch(
      () => integrations.services.value,
      (services) => {
        if (services.length === 0) return;

        const membraneKeys = new Set(
          services.map((s) => s.key).filter((k): k is string => !!k)
        );
        for (const allowedKey of Object.keys(dataCollectionsPerIntegration)) {
          if (!membraneKeys.has(allowedKey)) {
            window.console.warn(
              `[integrationAllowlist] Integration key "${allowedKey}" is in the allowlist but was not found in Membrane.`
            );
          }
        }
      }
    )
  );

  stopHandles.push(
    watch(
      [() => integrations.loading.value, () => mcp.loading.value],
      ([membraneLoading, mcpLoading]) => {
        loading.value = membraneLoading || mcpLoading;
      },
      { immediate: true }
    )
  );

  // --- Build items ---

  const knownDescriptions: Record<string, string> = {
    Asana: "Import tasks and projects from Asana.",
    Attio: "Sync your Attio tasks into Routine.",
    ClickUp: "Sync tasks from your ClickUp Lists.",
    Clickup: "Sync tasks from your ClickUp Lists.",
    "Microsoft To Do": "Sync tasks from Microsoft To Do.",
    "Microsoft Outlook": "Turn Outlook emails into tasks.",
    Todoist: "Import tasks and projects from Todoist.",
    Gmail: "Turn emails into actionable tasks.",
    GitHub: "Track issues and pull requests.",
    GitLab: "Sync your GitLab issues into Routine.",
    Jira: "Sync your Jira issues into Routine.",
    Gitlab: "Sync your GitLab issues into Routine.",
    Slack: "Create tasks from Slack messages.",
    Whatsapp: "Turn inbound WhatsApp messages into tasks.",
    WhatsApp: "Turn inbound WhatsApp messages into tasks.",
  };

  const descriptionFor = (app: Integration): string =>
    knownDescriptions[app.name] ||
    app.description ||
    `Integrate with ${app.name}.`;

  const connectedItems = computed<IntegrationItem[]>(() => {
    const items: IntegrationItem[] = [];

    if (isPersonalOrg.value && isGoogleWorkspaceActive.value) {
      items.push({
        id: "google-workspace",
        name: "Google Workspace Directory",
        description: "Access your organization's directory.",
        type: "google-workspace",
        onClick: configureGoogleWorkspace,
      });
    }

    // The native Notion integration is deprecated in favour of the Membrane one
    // (listed among `enabledApps` / `disabledApps`). It stays reachable for
    // users who already configured it — so they can migrate and disconnect —
    // but is labelled as deprecated and is no longer offered in
    // `availableItems`.
    if (isPersonalOrg.value && isNotionActive.value) {
      items.push({
        id: "notion",
        name: "Notion (deprecated)",
        description: "Switch to the new Notion integration to sync your tasks.",
        type: "notion",
        iconComponent: NotionIcon,
        onClick: configureNotion,
      });
    }

    enabledApps.value.forEach((app) => {
      items.push({
        id: app.id,
        name: app.name,
        description: descriptionFor(app),
        type: "membrane",
        nature: natureForApp(app),
        logoUri: app.logoUri,
        onClick: () => manageApp(app.id),
      });
    });

    return items;
  });

  const availableItems = computed<IntegrationItem[]>(() => {
    const items: IntegrationItem[] = [];

    if (isPersonalOrg.value) {
      if (!isGoogleWorkspaceActive.value) {
        items.push({
          id: "google-workspace",
          name: "Google Workspace Directory",
          description: "Access your organization's directory.",
          type: "google-workspace",
          onClick: configureGoogleWorkspace,
        });
      }

      // Deliberately not offering the native Notion integration here: users
      // without one should only discover the Membrane Notion app.

      items.push({
        id: "zapier",
        name: "Zapier",
        description: "Automate workflows with 7,000+ apps.",
        type: "zapier",
        iconComponent: ZapierIcon,
        helpUrl: "https://routine.co/integrations/services/zapier",
        onClick: () => openUrl("https://zapier.com/apps/routine/integrations"),
      });
    }

    disabledApps.value.forEach((app) => {
      items.push({
        id: app.id,
        name: app.name,
        description: descriptionFor(app),
        type: "membrane",
        nature: natureForApp(app),
        logoUri: app.logoUri,
        onClick: () => manageApp(app.id),
      });
    });

    return items;
  });

  const connectionsCount = computed(
    () => integrations.connections.value.length
  );

  const refreshConnections = async () => {
    await Promise.all([integrations.refresh(), mcp.refresh()]);
  };

  return {
    loading,
    error: integrations.error,
    connectedItems,
    availableItems,
    connectionsCount,
    isPersonalOrg,
    openUrl,
    refreshConnections,
    release,
  };
}
